import { Link } from 'react-router-dom'
import {
  Badge,
  Button,
  Label,
  PageContainer,
  PageHeader,
  PageHeaderLeading,
  Switch,
  formatRelativeTimeIso,
} from '@yeshuman/sdk'

import type { PlatformActionRun, PlatformActionStatus } from '../../hooks/use-platform-area'

export const PLATFORM_PAGE_SIZE = 25

const STATUS_BADGE: Record<
  PlatformActionStatus,
  { label: string; variant: 'successSoft' | 'destructiveSoft' | 'warningSoft' | 'secondarySoft' | 'defaultSoft' }
> = {
  running: { label: 'Running', variant: 'defaultSoft' },
  succeeded: { label: 'Done', variant: 'successSoft' },
  failed: { label: 'Failed', variant: 'destructiveSoft' },
  skipped: { label: 'Skipped', variant: 'secondarySoft' },
  interrupted: { label: 'Interrupted', variant: 'warningSoft' },
}

export function ActionStatusBadge({ status }: { status: PlatformActionStatus }) {
  const { label, variant } = STATUS_BADGE[status] ?? STATUS_BADGE.skipped
  return <Badge variant={variant}>{label}</Badge>
}

export function SimulatedBadge() {
  return <Badge variant="warningSoft">Simulated</Badge>
}

export function formatScore(score: number | null | undefined): string {
  return score === null || score === undefined ? 'Unscored' : `${Math.round(score * 100)}%`
}

export function actionLogPath(targetType: 'job' | 'profile', targetId: string): string {
  return `/platform/actions?target_type=${targetType}&target_id=${targetId}`
}

/** Latest Platform action on a row, linking to that row's history in the action log. */
export function LastActionCell({
  run,
  targetType,
  targetId,
}: {
  run: PlatformActionRun | null
  targetType: 'job' | 'profile'
  targetId: string
}) {
  if (!run) return <span className="text-sm text-muted-foreground">None yet</span>
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        <ActionStatusBadge status={run.status} />
        <span className="text-sm">{run.action_label}</span>
      </div>
      {run.summary ? <p className="max-w-xs text-xs text-muted-foreground">{run.summary}</p> : null}
      <Link
        to={actionLogPath(targetType, targetId)}
        className="text-xs text-primary underline-offset-4 hover:underline"
      >
        {formatRelativeTimeIso(run.started_at)} · history
      </Link>
    </div>
  )
}

export function IncludeSimulatedSwitch({
  id,
  checked,
  onCheckedChange,
}: {
  id: string
  checked: boolean
  onCheckedChange: (checked: boolean) => void
}) {
  return (
    <div className="flex items-center gap-2">
      <Switch id={id} checked={checked} onCheckedChange={onCheckedChange} />
      <Label htmlFor={id} className="text-sm font-normal">
        Include simulated
      </Label>
    </div>
  )
}

export function ListPager({
  total,
  offset,
  pageSize = PLATFORM_PAGE_SIZE,
  disabled,
  onOffsetChange,
}: {
  total: number
  offset: number
  pageSize?: number
  disabled?: boolean
  onOffsetChange: (offset: number) => void
}) {
  if (total <= pageSize) return null
  return (
    <div className="flex items-center gap-2">
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled || offset === 0}
        onClick={() => onOffsetChange(Math.max(0, offset - pageSize))}
      >
        Previous
      </Button>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled || offset + pageSize >= total}
        onClick={() => onOffsetChange(offset + pageSize)}
      >
        Next
      </Button>
      <span className="text-sm text-muted-foreground" role="status">
        {offset + 1}–{Math.min(offset + pageSize, total)} of {total}
      </span>
    </div>
  )
}

export function PlatformOnlyNotice({ title }: { title: string }) {
  return (
    <PageContainer className="max-w-5xl">
      <PageHeader leading={<PageHeaderLeading />} title={title} description="Platform group only." />
      <p className="text-sm text-muted-foreground">Switch to the Platform focus to use this page.</p>
    </PageContainer>
  )
}
